/**
 * MultiShiftImportForm - Multi-Shift Import UI
 * 
 * EF-SHIFT-IMPORT-MULTI-SHIFT-UI-V1: Extension of ShiftImportForm to handle
 * multiple shift candidates from a single email parse.
 * 
 * Consumes the output of /api/projects/parse-multi-shift:
 * {
 *   project_import: { client_name, client_id, project_name, start_date, end_date },
 *   shift_candidates: [ { name, date, start_time, ... }, ... ],
 *   import_batch_id: "uuid"
 * }
 * 
 * Features:
 * - Display project header with editable fields
 * - Display detected shifts in table format
 * - Edit/remove individual shift candidates
 * - Validation indicators for incomplete shifts
 * - Confirm Import creates project + all enabled shifts
 * - No database writes before user confirmation
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ClientSelect } from "../shared/ClientSelect";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Loader2,
  Calendar,
  Clock,
  MapPin,
  FolderKanban,
  Users,
  AlertCircle,
  Timer,
  CheckCircle,
  AlertTriangle,
  Building,
  FileText,
  Trash2,
  Edit2,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ListChecks,
  Package,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import api, { getClients, getVenues, getVenueLocations } from "../../lib/api";
// EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1: Import format guidance
import ImportFormatHintPanel from "../guidance/ImportFormatHintPanel";

/**
 * Status badge for shift candidates
 */
const ShiftStatusBadge = ({ status }) => {
  const styles = {
    complete: "bg-green-500/20 text-green-400 border-green-500/30",
    incomplete: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    error: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  
  const icons = {
    complete: <CheckCircle className="h-3 w-3" />,
    incomplete: <AlertTriangle className="h-3 w-3" />,
    error: <AlertCircle className="h-3 w-3" />,
  };
  
  return (
    <Badge variant="outline" className={cn("text-xs gap-1", styles[status] || styles.incomplete)}>
      {icons[status]}
      {status}
    </Badge>
  );
};

/**
 * Inline editable cell
 */
const EditableCell = ({ value, onChange, type = "text", disabled = false, error = false, className = "" }) => {
  return (
    <Input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={cn(
        "h-8 text-sm bg-transparent border-transparent hover:border-border focus:border-primary",
        error && "border-destructive",
        className
      )}
    />
  );
};

/**
 * Venue select cell
 */
const VenueSelectCell = ({ value, venues, onChange, disabled = false, error = false }) => {
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={cn("h-8 text-sm bg-transparent border-transparent hover:border-border", error && "border-destructive")}>
        <SelectValue placeholder="Select venue" />
      </SelectTrigger>
      <SelectContent>
        {venues.map((venue) => (
          <SelectItem key={venue.id} value={venue.id}>
            {venue.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

/**
 * Location select cell
 */
const LocationSelectCell = ({ value, locations, onChange, disabled = false }) => {
  if (locations.length === 0) {
    return <span className="text-xs text-muted-foreground">Select venue first</span>;
  }
  
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-8 text-sm bg-transparent border-transparent hover:border-border">
        <SelectValue placeholder="Select location" />
      </SelectTrigger>
      <SelectContent>
        {locations.map((loc) => (
          <SelectItem key={loc.id} value={loc.id}>
            {loc.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export function MultiShiftImportForm({
  isOpen,
  onClose,
  multiShiftData,  // Output from /api/projects/parse-multi-shift
  messageId,
  onSuccess,
}) {
  const navigate = useNavigate();
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dropdown data
  const [clients, setClients] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venueLocations, setVenueLocations] = useState([]);
  
  // Project form state (from project_import)
  const [projectData, setProjectData] = useState({
    client_id: "",
    client_name: "",
    project_name: "",
    start_date: "",
    end_date: "",
  });
  
  // Shift candidates state - local editable copy
  const [shiftCandidates, setShiftCandidates] = useState([]);
  
  // Import batch ID from parser
  const [importBatchId, setImportBatchId] = useState(null);
  
  // Expanded rows for details
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Validation errors
  const [errors, setErrors] = useState({});
  
  // Load dropdown data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsRes, venuesRes, locationsRes] = await Promise.all([
          getClients({ limit: 100 }),
          getVenues({ limit: 100 }),
          getVenueLocations({ limit: 500 }),
        ]);
        // API returns arrays directly via axios response.data
        setClients(clientsRes.data || []);
        setVenues(venuesRes.data || []);
        setVenueLocations(locationsRes.data || []);
        console.log('Loaded venue locations:', locationsRes.data?.length || 0);
      } catch (err) {
        console.error("Failed to load data:", err);
        toast.error("Failed to load form data");
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      setLoading(true);
      loadData();
    }
  }, [isOpen]);
  
  // Initialize from multi-shift parser data
  useEffect(() => {
    if (isOpen && multiShiftData && !loading) {
      const project = multiShiftData.project_import || {};
      const candidates = multiShiftData.shift_candidates || [];
      const batchId = multiShiftData.import_batch_id;
      
      // Set project data
      setProjectData({
        client_id: project.client_id || "",
        client_name: project.client_name || "",
        project_name: project.project_name || "",
        start_date: project.start_date || "",
        end_date: project.end_date || "",
      });
      
      // Set shift candidates with enabled flag
      setShiftCandidates(candidates.map((c, idx) => ({
        ...c,
        _enabled: c.status !== "error",  // Enable complete/incomplete, disable errors
        _localId: `shift-${idx}-${Date.now()}`,
      })));
      
      // Set batch ID
      setImportBatchId(batchId);
      
      // Try to match client if ID not found
      if (!project.client_id && project.client_name && clients.length > 0) {
        const matchedClient = clients.find(c => 
          c.name.toLowerCase() === project.client_name.toLowerCase()
        );
        if (matchedClient) {
          setProjectData(prev => ({ ...prev, client_id: matchedClient.id }));
        }
      }
    }
  }, [isOpen, multiShiftData, loading, clients]);
  
  // Get filtered locations for a venue
  const getLocationsForVenue = useCallback((venueId) => {
    if (!venueId) return [];
    return venueLocations.filter(vl => vl.venue_id === venueId);
  }, [venueLocations]);
  
  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const enabled = shiftCandidates.filter(c => c._enabled);
    const complete = enabled.filter(c => c.status === "complete");
    const incomplete = enabled.filter(c => c.status === "incomplete");
    const totalCrew = enabled.reduce((sum, c) => sum + (c.staff_required || 0), 0);
    const totalHours = enabled.reduce((sum, c) => sum + (c.duration_hours || 0), 0);
    
    return {
      total: shiftCandidates.length,
      enabled: enabled.length,
      complete: complete.length,
      incomplete: incomplete.length,
      disabled: shiftCandidates.length - enabled.length,
      totalCrew,
      totalHours,
    };
  }, [shiftCandidates]);
  
  // Toggle shift enabled state
  const toggleShiftEnabled = (localId) => {
    setShiftCandidates(prev => prev.map(c => 
      c._localId === localId ? { ...c, _enabled: !c._enabled } : c
    ));
  };
  
  // Remove shift from list
  const removeShift = (localId) => {
    setShiftCandidates(prev => prev.filter(c => c._localId !== localId));
  };
  
  // Update shift field
  const updateShiftField = (localId, field, value) => {
    setShiftCandidates(prev => prev.map(c => {
      if (c._localId !== localId) return c;
      
      const updated = { ...c, [field]: value };
      
      // Recalculate status based on required fields
      const hasName = !!updated.name;
      const hasDate = !!updated.date;
      const hasStartTime = !!updated.start_time;
      
      if (hasName && hasDate && hasStartTime) {
        updated.status = "complete";
        updated.missing_fields = [];
      } else {
        updated.status = "incomplete";
        updated.missing_fields = [];
        if (!hasName) updated.missing_fields.push("name");
        if (!hasDate) updated.missing_fields.push("date");
        if (!hasStartTime) updated.missing_fields.push("start_time");
      }
      
      return updated;
    }));
  };
  
  // Handle venue change for a shift
  const handleShiftVenueChange = (localId, venueId) => {
    const venue = venues.find(v => v.id === venueId);
    setShiftCandidates(prev => prev.map(c => {
      if (c._localId !== localId) return c;
      return {
        ...c,
        venue_id: venueId,
        venue_name: venue?.name || "",
        venue_location_id: "",  // Reset location when venue changes
        venue_location_name: "",
      };
    }));
  };
  
  // Handle location change for a shift
  const handleShiftLocationChange = (localId, locationId) => {
    const location = venueLocations.find(l => l.id === locationId);
    setShiftCandidates(prev => prev.map(c => {
      if (c._localId !== localId) return c;
      return {
        ...c,
        venue_location_id: locationId,
        venue_location_name: location?.name || "",
      };
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Project validation
    if (!projectData.client_id) newErrors.client_id = "Client is required";
    if (!projectData.project_name) newErrors.project_name = "Project name is required";
    
    // Check enabled shifts have required fields
    const enabledShifts = shiftCandidates.filter(c => c._enabled);
    if (enabledShifts.length === 0) {
      newErrors.shifts = "At least one shift must be enabled";
    }
    
    const shiftsWithoutVenue = enabledShifts.filter(s => !s.venue_id);
    const shiftsWithoutLocation = enabledShifts.filter(s => !s.venue_location_id);
    
    if (shiftsWithoutVenue.length > 0) {
      newErrors.venue = `${shiftsWithoutVenue.length} shift(s) missing venue`;
    }
    
    if (shiftsWithoutLocation.length > 0) {
      newErrors.location = `${shiftsWithoutLocation.length} shift(s) missing location`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle confirm import
  const handleConfirmImport = async () => {
    if (!validateForm()) {
      toast.error("Please fix validation errors before importing");
      return;
    }
    
    const enabledShifts = shiftCandidates.filter(c => c._enabled);
    
    // Final venue/location validation
    const shiftsWithoutVenue = enabledShifts.filter(s => !s.venue_id || !s.venue_location_id);
    if (shiftsWithoutVenue.length > 0) {
      toast.error("All enabled shifts must have venue and location selected");
      return;
    }
    
    setSaving(true);
    
    try {
      // EF-IMPORT-DUPLICATE-PROTECTION-V1: Check for duplicate import first
      if (messageId) {
        try {
          const duplicateCheck = await api.post('/projects/check-duplicate-import', {
            source_message_id: messageId
          });
          
          if (duplicateCheck.data.is_duplicate) {
            const existingRef = duplicateCheck.data.existing_project_reference;
            const existingId = duplicateCheck.data.existing_project_id;
            
            toast.error(
              <div className="space-y-2">
                <div>This email has already been imported.</div>
                <div className="text-sm opacity-80">
                  Existing project: {existingRef}
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    onClose();
                    navigate(`/projects/${existingId}`);
                  }}
                  className="mt-1"
                >
                  View Existing Project
                </Button>
              </div>,
              { duration: 10000 }
            );
            setSaving(false);
            return;
          }
        } catch (dupErr) {
          // If check fails, continue with import (fail-open for now)
          console.warn('Duplicate check failed:', dupErr);
        }
      }
      
      // Create project with import traceability
      const projectPayload = {
        client_id: projectData.client_id,
        name: projectData.project_name,
        start_date: projectData.start_date || new Date().toISOString().split('T')[0],
        end_date: projectData.end_date || projectData.start_date || new Date().toISOString().split('T')[0],
        status: "draft",
      };
      
      // EF-IMPORT-DUPLICATE-PROTECTION-V1: Add import traceability
      if (messageId) {
        projectPayload.source_message_id = messageId;
      }
      if (importBatchId) {
        projectPayload.import_batch_id = importBatchId;
      }
      
      const projectResponse = await api.post('/projects', projectPayload);
      
      const projectId = projectResponse.data.id;
      const projectRef = projectResponse.data.reference_number;
      
      // Create each enabled shift
      let shiftsCreated = 0;
      let shiftsSkipped = 0;
      let rulesApplied = 0;
      const shiftResults = [];
      
      for (const shift of enabledShifts) {
        try {
          // Calculate end_time from start_time + duration
          let endTime = shift.end_time;
          if (!endTime && shift.start_time && shift.duration_hours) {
            const [h, m] = shift.start_time.split(':').map(Number);
            const endHour = h + Math.floor(shift.duration_hours);
            const endMin = m + ((shift.duration_hours % 1) * 60);
            endTime = `${String(endHour % 24).padStart(2, '0')}:${String(Math.floor(endMin)).padStart(2, '0')}`;
          }
          
          const shiftResponse = await api.post('/shifts/with-rules', {
            project_id: projectId,
            name: shift.name,
            venue_id: shift.venue_id,
            venue_location_id: shift.venue_location_id,
            date: shift.date,
            start_time: shift.start_time,
            end_time: endTime || "17:00",
            duration_hours: shift.duration_hours || 8,
            staff_required: shift.staff_required || 1,
            status: "draft",
            notes: shift.notes || "",
          });
          
          shiftsCreated++;
          
          // Track rules applied
          if (shiftResponse.data.rule_prompts?.length > 0) {
            rulesApplied += shiftResponse.data.rule_prompts.length;
          }
          
          shiftResults.push({
            name: shift.name,
            id: shiftResponse.data.shift?.id,
            success: true,
          });
        } catch (shiftErr) {
          console.error(`Failed to create shift ${shift.name}:`, shiftErr);
          
          // EF-IMPORT-DUPLICATE-PROTECTION-V1: Handle duplicate shift gracefully
          if (shiftErr.response?.status === 409) {
            shiftsSkipped++;
            shiftResults.push({
              name: shift.name,
              success: false,
              skipped: true,
              error: "Duplicate shift skipped",
            });
          } else {
            shiftResults.push({
              name: shift.name,
              success: false,
              error: shiftErr.response?.data?.detail || shiftErr.message,
            });
          }
        }
      }
      
      // Mark inbox message as processed if messageId provided
      if (messageId) {
        try {
          await api.patch(`/inbox/messages/${messageId}`, {
            status: "processed",
            project_created: true,
            project_id: projectId,
          });
        } catch (err) {
          console.warn("Failed to update inbox message status:", err);
        }
      }
      
      // Show success message
      const failedCount = shiftResults.filter(r => !r.success && !r.skipped).length;
      const skippedCount = shiftResults.filter(r => r.skipped).length;
      
      if (failedCount > 0) {
        toast.warning(`Created ${shiftsCreated} of ${enabledShifts.length} shifts. ${failedCount} failed.`);
      } else if (skippedCount > 0) {
        const rulesMsg = rulesApplied > 0 ? ` (${rulesApplied} rules applied)` : '';
        toast.success(`Created project ${projectRef} with ${shiftsCreated} shifts${rulesMsg}. ${skippedCount} duplicate(s) skipped.`);
      } else {
        const rulesMsg = rulesApplied > 0 ? ` (${rulesApplied} rules applied)` : '';
        toast.success(`Created project ${projectRef} with ${shiftsCreated} shifts${rulesMsg}`);
      }
      
      onSuccess?.({
        project_id: projectId,
        project_reference: projectRef,
        shifts_created: shiftsCreated,
        import_batch_id: importBatchId,
      });
      
      onClose();
      navigate(`/projects/${projectId}`);
      
    } catch (err) {
      console.error("Import failed:", err);
      
      // EF-IMPORT-DUPLICATE-PROTECTION-V1: Handle 409 duplicate error
      if (err.response?.status === 409 && err.response?.data?.detail?.existing_project_id) {
        const detail = err.response.data.detail;
        toast.error(
          <div className="space-y-2">
            <div>{detail.message || "This email has already been imported."}</div>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => {
                onClose();
                navigate(`/projects/${detail.existing_project_id}`);
              }}
              className="mt-1"
            >
              View Existing Project ({detail.existing_project_reference})
            </Button>
          </div>,
          { duration: 10000 }
        );
      } else {
        const errorMessage = typeof err.response?.data?.detail === 'string' 
          ? err.response.data.detail 
          : "Failed to import shifts";
        toast.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    onClose();
  };
  
  // Check if we can import
  const canImport = useMemo(() => {
    const hasClient = !!projectData.client_id;
    const hasProjectName = !!projectData.project_name;
    const enabledShifts = shiftCandidates.filter(c => c._enabled);
    const hasShifts = enabledShifts.length > 0;
    const allHaveVenue = enabledShifts.every(s => s.venue_id && s.venue_location_id);
    
    return hasClient && hasProjectName && hasShifts && allHaveVenue;
  }, [projectData, shiftCandidates]);
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" data-testid="multi-shift-import-title">
            <ListChecks className="h-5 w-5 text-primary" />
            Import Multiple Shifts
          </DialogTitle>
          <DialogDescription>
            {multiShiftData?.parse_metadata?.total_blocks_detected || 0} shift(s) detected. 
            Review and edit before importing.
          </DialogDescription>
          {/* EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1: Format guidance in header */}
          <ImportFormatHintPanel className="mt-3" />
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Project Header Section */}
            <div className="px-6 py-4 border-b bg-surface-elevated/50">
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <FolderKanban className="h-4 w-4" />
                Project Details
              </h3>
              <div className="grid grid-cols-4 gap-4">
                {/* Client */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    Client *
                  </Label>
                  <ClientSelect
                    clients={clients}
                    value={projectData.client_id}
                    onChange={(v) => setProjectData(prev => ({ ...prev, client_id: v || '' }))}
                    placeholder="Search or select client..."
                    testId="project-client-select"
                    className={errors.client_id ? "border-destructive rounded-md" : ""}
                  />
                  {errors.client_id && <p className="text-xs text-destructive">{errors.client_id}</p>}
                </div>
                
                {/* Project Name */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    Project Name *
                  </Label>
                  <Input
                    value={projectData.project_name}
                    onChange={(e) => setProjectData(prev => ({ ...prev, project_name: e.target.value }))}
                    placeholder="Enter project name"
                    className={cn("h-9", errors.project_name && "border-destructive")}
                    data-testid="project-name-input"
                  />
                  {errors.project_name && <p className="text-xs text-destructive">{errors.project_name}</p>}
                </div>
                
                {/* Start Date */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={projectData.start_date}
                    onChange={(e) => setProjectData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="h-9"
                    data-testid="project-start-date"
                  />
                </div>
                
                {/* End Date */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={projectData.end_date}
                    onChange={(e) => setProjectData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="h-9"
                    data-testid="project-end-date"
                  />
                </div>
              </div>
            </div>
            
            {/* Shifts Table Section */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="px-6 py-3 flex items-center justify-between border-b">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Detected Shifts ({shiftCandidates.length})
                </h3>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-400" />
                    {summaryStats.complete} complete
                  </span>
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 text-amber-400" />
                    {summaryStats.incomplete} incomplete
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {summaryStats.totalCrew} total crew
                  </span>
                </div>
              </div>
              
              {/* Validation errors */}
              {(errors.shifts || errors.venue || errors.location) && (
                <div className="px-6 py-2 bg-destructive/10 border-b border-destructive/20">
                  <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    {errors.shifts || errors.venue || errors.location}
                  </div>
                </div>
              )}
              
              <ScrollArea className="flex-1">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={shiftCandidates.every(c => c._enabled)}
                          onCheckedChange={(checked) => {
                            setShiftCandidates(prev => prev.map(c => ({ ...c, _enabled: !!checked })));
                          }}
                        />
                      </TableHead>
                      <TableHead className="w-16">Status</TableHead>
                      <TableHead className="min-w-[150px]">Shift Name</TableHead>
                      <TableHead className="min-w-[150px]">Venue</TableHead>
                      <TableHead className="min-w-[120px]">Location</TableHead>
                      <TableHead className="w-28">Date</TableHead>
                      <TableHead className="w-20">Start</TableHead>
                      <TableHead className="w-16">Hours</TableHead>
                      <TableHead className="w-16">Crew</TableHead>
                      <TableHead className="w-16">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shiftCandidates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                          No shifts detected in email
                        </TableCell>
                      </TableRow>
                    ) : (
                      shiftCandidates.map((shift) => (
                        <TableRow 
                          key={shift._localId}
                          className={cn(
                            !shift._enabled && "opacity-50 bg-muted/20",
                            shift.status === "error" && "bg-destructive/5"
                          )}
                          data-testid={`shift-row-${shift.block_index}`}
                        >
                          {/* Enable checkbox */}
                          <TableCell>
                            <Checkbox
                              checked={shift._enabled}
                              onCheckedChange={() => toggleShiftEnabled(shift._localId)}
                              disabled={shift.status === "error"}
                              data-testid={`shift-enable-${shift.block_index}`}
                            />
                          </TableCell>
                          
                          {/* Status */}
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <ShiftStatusBadge status={shift.status} />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {shift.missing_fields?.length > 0 ? (
                                    <span>Missing: {shift.missing_fields.join(", ")}</span>
                                  ) : shift.errors?.length > 0 ? (
                                    <span>{shift.errors[0]}</span>
                                  ) : (
                                    <span>All required fields present</span>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          
                          {/* Name */}
                          <TableCell>
                            <EditableCell
                              value={shift.name}
                              onChange={(v) => updateShiftField(shift._localId, 'name', v)}
                              disabled={!shift._enabled}
                              error={shift.missing_fields?.includes('name')}
                            />
                          </TableCell>
                          
                          {/* Venue */}
                          <TableCell>
                            <VenueSelectCell
                              value={shift.venue_id}
                              venues={venues}
                              onChange={(v) => handleShiftVenueChange(shift._localId, v)}
                              disabled={!shift._enabled}
                              error={shift._enabled && !shift.venue_id}
                            />
                          </TableCell>
                          
                          {/* Location */}
                          <TableCell>
                            <LocationSelectCell
                              value={shift.venue_location_id}
                              locations={getLocationsForVenue(shift.venue_id)}
                              onChange={(v) => handleShiftLocationChange(shift._localId, v)}
                              disabled={!shift._enabled || !shift.venue_id}
                            />
                          </TableCell>
                          
                          {/* Date */}
                          <TableCell>
                            <EditableCell
                              type="date"
                              value={shift.date}
                              onChange={(v) => updateShiftField(shift._localId, 'date', v)}
                              disabled={!shift._enabled}
                              error={shift.missing_fields?.includes('date')}
                            />
                          </TableCell>
                          
                          {/* Start Time */}
                          <TableCell>
                            <EditableCell
                              type="time"
                              value={shift.start_time}
                              onChange={(v) => updateShiftField(shift._localId, 'start_time', v)}
                              disabled={!shift._enabled}
                              error={shift.missing_fields?.includes('start_time')}
                            />
                          </TableCell>
                          
                          {/* Hours */}
                          <TableCell>
                            <EditableCell
                              type="number"
                              value={shift.duration_hours}
                              onChange={(v) => updateShiftField(shift._localId, 'duration_hours', parseFloat(v) || 8)}
                              disabled={!shift._enabled}
                              className="w-14"
                            />
                          </TableCell>
                          
                          {/* Crew */}
                          <TableCell>
                            <EditableCell
                              type="number"
                              value={shift.staff_required}
                              onChange={(v) => updateShiftField(shift._localId, 'staff_required', parseInt(v) || 1)}
                              disabled={!shift._enabled}
                              className="w-14"
                            />
                          </TableCell>
                          
                          {/* Actions */}
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                    onClick={() => removeShift(shift._localId)}
                                    data-testid={`shift-remove-${shift.block_index}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Remove shift</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
            
            {/* Summary Footer */}
            <div className="px-6 py-3 border-t bg-surface-elevated/50 flex items-center justify-between">
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <span className="text-muted-foreground">Import Batch:</span>
                  <code className="text-xs bg-muted px-2 py-0.5 rounded">
                    {importBatchId?.substring(0, 8) || "N/A"}
                  </code>
                </div>
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center gap-4">
                  <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">{summaryStats.enabled}</span> shifts to import
                  </span>
                  <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">{summaryStats.totalCrew}</span> crew
                  </span>
                  <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">{summaryStats.totalHours}</span> hours
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmImport} 
            disabled={saving || loading || !canImport}
            className="bg-primary hover:bg-primary/90"
            data-testid="confirm-import-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm Import ({summaryStats.enabled} shifts)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MultiShiftImportForm;
